"use client";

import { useState } from "react";

/**
 * Share dialog — gives the owner a copyable public link to the pack.
 * The link opens a read-only shared view that anyone can open.
 */
export default function SharePackDialog({
  packId,
  onClose,
}: {
  packId: string;
  onClose: () => void;
}) {
  const url = `${typeof window !== "undefined" ? window.location.origin : ""}/agentic-workflows/rfp-packs/shared/${packId}`;
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Share RFP pack"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 120,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
        backdropFilter: "blur(8px)",
        animation: "fadeIn .2s ease",
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--pk-paper)",
          border: "1px solid var(--pk-line-strong)",
          borderRadius: 20,
          padding: "32px 28px 24px",
          maxWidth: 480,
          width: "100%",
          textAlign: "center",
          boxShadow: "0 24px 60px rgba(0,0,0,0.35)",
          animation: "popIn .25s ease",
        }}
      >
        <h3 style={{ margin: "0 0 8px", fontSize: 20, fontWeight: 600, color: "var(--pk-ink)", textAlign: "left" }}>
          Share this RFP
        </h3>
        <p style={{ margin: "0 0 16px", color: "var(--pk-muted)", fontSize: 13.5, lineHeight: 1.6, textAlign: "left" }}>
          Anyone with this link can open a read-only view of the pack and leave feedback to help you make it issue-ready.
        </p>
        <div
          style={{
            display: "flex",
            gap: 8,
            border: "1px solid var(--pk-line-strong)",
            borderRadius: 12,
            padding: "8px 8px 8px 14px",
            alignItems: "center",
          }}
        >
          <input
            readOnly
            value={url}
            onFocus={(e) => e.currentTarget.select()}
            style={{ flex: 1, border: "none", background: "none", color: "var(--pk-ink)", fontSize: 13, outline: "none" }}
          />
          <button
            type="button"
            onClick={() => void copy()}
            style={{
              border: "none",
              borderRadius: 10,
              padding: "10px 16px",
              fontSize: 13,
              cursor: "pointer",
              fontWeight: 650,
              color: "#fff",
              background: "linear-gradient(135deg, var(--indigo), var(--magenta))",
              whiteSpace: "nowrap",
            }}
          >
            {copied ? "Copied!" : "Copy link"}
          </button>
        </div>
        <button
          type="button"
          onClick={onClose}
          style={{ background: "none", border: "none", color: "var(--pk-muted)", fontSize: 13, cursor: "pointer", marginTop: 16, display: "block", width: "100%" }}
        >
          Done
        </button>
      </div>
    </div>
  );
}